"use client";

import { Transition } from "@headlessui/react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react";
import { forwardRef, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { formatBytes } from "../../formatters/formatBytes";
import { formatList } from "../../formatters/formatList";
import { UploadIcon } from "../../icons/UploadIcon";
import { Text } from "../../typography/Text";
import { classNames } from "../../utils/classNames";
import { uniqueId } from "../../utils/unique-id";
import { LinkButton } from "../Button";
import { FormControl } from "../FormControl";
import type { BaseFormProps } from "../types";
import { FileItem } from "./FileItem";
import type { FileListProps } from "./FileList";
import { FileList } from "./FileList";
import type { FileUploadFile } from "./types";
import type { AcceptedFileType } from "./utils/getDropzoneFileRestrictions";
import {
	getDropzoneFileTypes,
	getFileExtensionsByFileTypes,
	getMaxDefaultMaxFileSizeByFileTypes,
} from "./utils/getDropzoneFileRestrictions";

const UPLOAD_ERROR_CODES = {
	INVALID_TYPE: "file-invalid-type",
	TOO_LARGE: "file-too-large",
	TOO_MANY_FILES: "too-many-files",
} as const;

const messages = {
	tooManyFilesSingle: msg({ message: "Too many files (1 maximum)" }),
	tooManyFiles: msg({ message: "Too many files" }),
	invalidType: msg({ message: "File must be of the following types: {data}" }),
	tooLarge: msg({ message: "File is too big (max {data})" }),
	tooManyFilesMax: msg({ message: "Too many files ({data} maximum)" }),
	unableToUpload: msg({ message: "File was unable to be uploaded: {code}" }),
	recommendedSize: msg({ message: "Recommended size of at least {0}px by {1}px" }),
	maximumSize: msg({ message: "({0} maximum)" }),
	dropFiles: msg({ message: "Drop your file(s) here or <0>Browse</0>" }),
	dropFile: msg({ message: "Drop your file here or <0>Browse</0>" }),
};

export type FileUploadProps = BaseFormProps & {
	maxFileSize?: number;
	imageDimensions?: { width: number; height: number };
	files: FileUploadFile[] | null;
	onAddFiles: (files: FileUploadFile[]) => Promise<void>;
	onRemoveFile: FileListProps["onRemoveFile"];
	acceptedFileTypes: AcceptedFileType[];
	multiple?: boolean;
	disabled?: boolean;
};

const _FileUpload = (props: FileUploadProps, _: React.ForwardedRef<HTMLInputElement>) => {
	const { i18n } = useLingui();
	const {
		files,
		onAddFiles,
		onRemoveFile,
		acceptedFileTypes,
		name,
		error: externalError,
		isRequired,
		label,
		multiple = false,
		imageDimensions,
		disabled,
	} = props;
	const [error, setError] = useState<string | undefined>(externalError);
	const acceptedFileExtensions = useMemo(() => getFileExtensionsByFileTypes(acceptedFileTypes), [acceptedFileTypes]);
	const acceptedFileExtensionsList = useMemo(
		() => formatList({ values: acceptedFileExtensions, locale: i18n.locale, type: "disjunction" }),
		[acceptedFileExtensions, i18n.locale],
	);
	const maxFileSize = useMemo(() => {
		return props.maxFileSize ?? getMaxDefaultMaxFileSizeByFileTypes(acceptedFileTypes);
	}, [props.maxFileSize, acceptedFileTypes]);

	const { getInputProps, getRootProps, isDragActive } = useDropzone({
		accept: getDropzoneFileTypes(acceptedFileTypes),
		multiple,
		maxSize: maxFileSize,
		disabled,
		onDropAccepted: async (acceptedFiles) => {
			if (acceptedFiles.length === 0) {
				return;
			}

			await onAddFiles(
				acceptedFiles.map((x) => ({
					file: x,
					progress: 0,
					name: x.name,
					size: x.size,
					url: URL.createObjectURL(x),
					key: uniqueId(),
					type: x.type,
				})),
			);
		},
		onDropRejected: async (rejected) => {
			if (rejected.length > 1 && !multiple) {
				// Only one file is allowed
				setError(i18n._(messages.tooManyFilesSingle));
			} else {
				await onAddFiles(
					rejected.map((x) => {
						const errors: string[] = [];
						for (const error of x.errors) {
							const code = error.code;
							let data: string | undefined = undefined;
							switch (code) {
								case UPLOAD_ERROR_CODES.INVALID_TYPE:
									data = acceptedFileExtensionsList;
									break;
								case UPLOAD_ERROR_CODES.TOO_LARGE:
									data = formatBytes(maxFileSize);
									break;
								case UPLOAD_ERROR_CODES.TOO_MANY_FILES:
									data = i18n._(messages.tooManyFiles);
									break;
								default:
									data = undefined;
							}

							if (data) {
								let message: string;
								switch (code) {
									case UPLOAD_ERROR_CODES.INVALID_TYPE:
										message = i18n._(
											messages.invalidType.id,
											{ data },
											{ message: messages.invalidType.message },
										);
										break;
									case UPLOAD_ERROR_CODES.TOO_LARGE:
										message = i18n._(
											messages.tooLarge.id,
											{ data },
											{ message: messages.tooLarge.message },
										);
										break;
									case UPLOAD_ERROR_CODES.TOO_MANY_FILES:
										message = i18n._(
											messages.tooManyFilesMax.id,
											{ data },
											{ message: messages.tooManyFilesMax.message },
										);
										break;
									default:
										message = i18n._(
											messages.unableToUpload.id,
											{ code },
											{ message: messages.unableToUpload.message },
										);
								}
								errors.push(message);
							} else {
								errors.push(
									i18n._(
										messages.unableToUpload.id,
										{ code },
										{ message: messages.unableToUpload.message },
									),
								);
							}
						}

						return {
							file: x.file,
							name: x.file.name,
							size: x.file.size,
							url: URL.createObjectURL(x.file),
							key: uniqueId(),
							error: errors?.join(". "),
							progress: 0,
							type: x.file.type,
						};
					}),
				);
			}
		},
	});

	return (
		<div className="flex flex-col gap-4">
			<FormControl
				name={name}
				error={error || externalError}
				label={label}
				isRequired={isRequired}
				description={props.description}
				formHelperText={props.formHelperText}
			>
				<Transition
					as="div"
					show={true}
					enter="transition duration-100 ease-out"
					enterFrom="transform scale-95 opacity-0"
					enterTo="transform scale-100 opacity-100"
					leave="transition duration-75 ease-out"
					leaveFrom="transform scale-100 opacity-100"
					leaveTo="transform scale-95 opacity-0"
				>
					{(multiple || files?.length === 0) && (
						<div
							{...getRootProps()}
							className={classNames(
								"flex min-h-fit items-center gap-2 rounded-md border border-neutral border-dashed p-4 text-sm",
								isDragActive ? "bg-neutral-faded" : "bg-white",
								disabled ? "cursor-not-allowed opacity-50" : undefined,
							)}
						>
							<input {...getInputProps()} />

							<div className="flex grow flex-col flex-wrap items-center justify-center gap-3">
								<UploadIcon className="h-8 w-8" />
								<div className="flex flex-col flex-wrap items-center">
									<Text>
										<Trans
											id={multiple ? messages.dropFiles.id : messages.dropFile.id}
											message={multiple ? messages.dropFiles.message : messages.dropFile.message}
											components={{
												0: <LinkButton size="sm" type="button" className="ml-1 text-base" />,
											}}
										/>
									</Text>
								</div>
								{!!imageDimensions && (
									<div className="flex items-center gap-1 text-center">
										<Text size="xs">
											{i18n._(
												messages.recommendedSize.id,
												{
													0: String(imageDimensions.width),
													1: String(imageDimensions.height),
												},
												{ message: messages.recommendedSize.message },
											)}
										</Text>
									</div>
								)}
								<div className="flex items-center gap-1 text-center">
									<Text size="xs">{acceptedFileExtensionsList}</Text>
									<Text size="xs">
										{i18n._(
											messages.maximumSize.id,
											{ 0: formatBytes(maxFileSize) },
											{ message: messages.maximumSize.message },
										)}
									</Text>
								</div>
							</div>
						</div>
					)}
					{!multiple && files?.[0] && (
						<FileItem showFilePreview onRemoveFile={onRemoveFile} file={files[0]} isInteractive />
					)}
				</Transition>
			</FormControl>
			<Transition
				as="div"
				show={multiple && (files ?? []).length > 0}
				enter="transition duration-100 ease-out"
				enterFrom="transform scale-95 opacity-0"
				enterTo="transform scale-100 opacity-100"
				leave="transition duration-75 ease-out"
				leaveFrom="transform scale-100 opacity-100"
				leaveTo="transform scale-95 opacity-0"
			>
				<FileList onRemoveFile={onRemoveFile} files={files ?? []} />
			</Transition>
		</div>
	);
};

_FileUpload.displayName = "FileUpload";

export const FileUpload = forwardRef(_FileUpload);
